import React, { useState, useEffect } from 'react';
import { Filter, Plus, Upload } from 'lucide-react';
import { EnhancedActionButton } from '../../../components/ui/enhanced-page-layout';
import { TableFilterLayout } from '../../../components/ui/table-filter-layout';
import UsersTable from './UsersTable';
import { BulkUploadDialog } from '../../bulk-upload/components/BulkUploadDialog';
import { userConfig } from '../../bulk-upload/configs';
import { useBulkUploadEnabled } from '../../hooks/useBulkUploadEnabled';
import { adminCreateUser } from 'wasp/client/operations';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from '../../../hooks/use-toast';

const UsersDashboardPage = () => {
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Add User dialog state
  const [showAddUser, setShowAddUser] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addUsername, setAddUsername] = useState('');
  const [addRole, setAddRole] = useState<'USER' | 'MODERATOR' | 'ADMINISTRATOR'>('USER');
  const [isAdding, setIsAdding] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const handleAddUser = () => {
    setAddEmail('');
    setAddUsername('');
    setAddRole('USER');
    setShowAddUser(true);
  };

  const handleAddUserSubmit = async () => {
    if (!addEmail.trim()) {
      toast({ title: 'Email is required', variant: 'destructive' });
      return;
    }
    setIsAdding(true);
    try {
      await adminCreateUser({
        email: addEmail.trim(),
        username: addUsername.trim() || undefined,
        platformRole: addRole,
      });
      toast({ title: 'User created', description: `${addEmail} has been created and sent a welcome email.` });
      setShowAddUser(false);
      setTableKey(k => k + 1); // force table refresh
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to create user', variant: 'destructive' });
    } finally {
      setIsAdding(false);
    }
  };

  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {isDesktop ? (
        <div className="w-full">
          {/* Fixed Header */}
          <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-0.5 sm:mt-1">Manage user accounts and permissions</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowFilters(f => !f)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md border transition-colors ${
                      showFilters
                        ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Filter
                  </button>
                  {isBulkUploadEnabled && (
                    <EnhancedActionButton
                      onClick={() => setShowBulkUpload(true)}
                      icon={Upload}
                      label="Bulk"
                      variant="secondary"
                      className="px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
                    />
                  )}
                  <EnhancedActionButton 
                    onClick={handleAddUser} 
                    icon={Plus} 
                    label="Add" 
                    className="px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Spacer to push content below fixed header */}
          <div className="h-28 sm:h-20" />

          {/* Table Header with Filters */}
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'User Details', span: 6 },
              { title: 'Primary Location', span: 4 },
              { title: 'Status', span: 2 },
            ]}
            filters={[
              {
                span: 6,
                content: (
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ),
              },
              { span: 4, content: <div className="h-10" /> },
              {
                span: 2,
                content: (
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Roles</option>
                    <option value="PLATFORM_OWNER">Platform Owner</option>
                    <option value="ADMINISTRATOR">Administrator</option>
                    <option value="MODERATOR">Moderator</option>
                    <option value="USER">User</option>
                  </select>
                ),
              },
            ]}
          />

          <UsersTable
            key={tableKey}
            viewMode="table"
            searchTerm={searchTerm}
            roleFilter={roleFilter === 'all' ? undefined : roleFilter}
          />
        </div>
      ) : (
        <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-sidebar-active">
          {/* Mobile Header */}
          <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 shrink-0">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Users</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Manage platform memberships</p>
          </div>

          {/* Mobile Action Row */}
          <div className="px-8 py-4 bg-gray-50/50 dark:bg-sidebar-hover/30 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3 shrink-0 uppercase">
            <Button onClick={handleAddUser} className="bg-blue-600 text-white rounded-xl px-8 h-11 font-semibold">Add User</Button>
            {isBulkUploadEnabled && (
              <Button onClick={() => setShowBulkUpload(true)} variant="outline" className="rounded-xl border-2 px-8 h-11 font-semibold">Bulk Upload</Button>
            )}
          </div>

          <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
            <div className="flex-1 overflow-auto">
              <UsersTable
                key={tableKey}
                viewMode="cards"
                searchTerm={searchTerm}
                roleFilter={roleFilter === 'all' ? undefined : roleFilter}
              />
            </div>

            {/* Mobile Filter Sidebar (stacks or side) */}
            <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-100 dark:border-gray-800 bg-white dark:bg-sidebar-active p-8 shrink-0 space-y-8 overflow-auto">
              <div className="space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Quick Search</h3>
                <Input 
                  placeholder="Search..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-gray-50 dark:bg-sidebar-hover border-none h-12 rounded-xl"
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Filter by Role</h3>
                <div className="grid grid-cols-1 gap-2">
                  {['all', 'PLATFORM_OWNER', 'ADMINISTRATOR', 'MODERATOR', 'USER'].map((role) => (
                    <button
                      key={role}
                      onClick={() => setRoleFilter(role)}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        roleFilter === role 
                          ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-600' 
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <span className="capitalize">{role === 'all' ? 'All Roles' : role.replace('_', ' ').toLowerCase()}</span>
                      {roleFilter === role && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shared Dialogs */}
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={userConfig}
        onComplete={() => {}}
      />

      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label htmlFor="add-email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="add-email"
                type="email"
                placeholder="user@example.com"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-username">Username <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                id="add-username"
                placeholder="username"
                value={addUsername}
                onChange={(e) => setAddUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={addRole} onValueChange={(v) => setAddRole(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMINISTRATOR">Administrator</SelectItem>
                  <SelectItem value="MODERATOR">Moderator</SelectItem>
                  <SelectItem value="USER">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)} disabled={isAdding}>Cancel</Button>
            <Button onClick={handleAddUserSubmit} disabled={isAdding || !addEmail.trim()}>
              {isAdding ? 'Creating…' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersDashboardPage;



